import { ErrorView } from "common/components/error";
import { FetchErrorRetry } from "common/helpers/errors";
import { Component } from "react";
import debounce from "lodash-es/debounce";

import { Highcharts } from "extensions/highcharts";
import {
    HighchartsChart,
    Chart,
    PieSeries,
    HighchartsProvider,
    Tooltip as HighchartsTooltip
} from "react-jsx-highcharts";

import { number, rate } from "common/formats";
import { toHex } from "common/colors";
import { Tooltip } from "common/components/tooltip";

import { Loader } from "common/helpers/HorizontalLoader";
import { references } from "common/helpers/references";
import { fetchChartData, drillThrough } from "../widgetHelper";

const colorsArr = [
    toHex("wcag-blue"), // 0
    toHex("wcag-yellow"), // 1
    toHex("wcag-teal"), // 2
    toHex("wcag-magenta"), // 3
    toHex("paz-black-l-30") // 4
];

export class DoublePivotPieChart extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loaded: false,
            error: false
        };
        this.resizeMethod = debounce(this.reflowChart.bind(this), 300);
    }
    
    componentDidMount() {
        if (this.existingPromiseRequest) {
            this.existingPromiseRequest.cancel();
        }
        
        this.existingPromiseRequest = Promise.map(this.props.dateRanges, (r) => fetchChartData(r, this.props))
            .then((data) => {
                data[0] = data[0].filter((r) => r.declines_count > 0);
                const processed = this.processData(data);
                this.setState({
                    loaded: true,
                    error: false,
                    data: processed
                });
            }, (error) => {
                this.setState({
                    loaded: true,
                    error: true
                });
            });
        
        window.addEventListener("resize", this.resizeMethod);
    }
    
    componentWillUnmount() {
        // Cancel any pending debounce func
        this.resizeMethod.cancel();
        
        // And then remove an event listener to prevent leaks
        window.removeEventListener("resize", this.resizeMethod);
        
        if (this.existingPromiseRequest) {
            this.existingPromiseRequest.cancel();
        }
    }
    
    getChart = (chart) => {
        this.chart = chart;
    };
    
    getAPIEndpoint() {
        const endpoint = this.props.subModel.get("api_endpoint");
        const pivot = this.props.second_pivot_key;
        
        return typeof endpoint === "function" ? endpoint(pivot) : endpoint;
    }
    
    getAvailableHeight() {
        const container = this.props.widgetContainer;
        const headerH = container.querySelector(".widgetTitle").clientHeight;
        const tabsH = container.querySelector(".nav-tabs").clientHeight;
        const summaryBars = Array.from(container.querySelectorAll(".summary-bar")).find((sb) => sb && sb.clientHeight > 0);
        const summaryH = summaryBars?.clientHeight || 0;
        
        return container.clientHeight - (headerH + tabsH + summaryH);
    }
    
    getPieData() {
        const srcData = this.state.data;
        const total = srcData.current.reduce((t, currentValue) => t + currentValue);
        let percentTotal = 0;
        const pieData = [];
        srcData.keys.forEach((k, i) => {
            const description = references.toAlias(k, this.props.second_pivot_key);
            let percent;
            if (i === 4) {
                percent = 100 - percentTotal;
            } else {
                percent = Math.round((srcData.current[i] / total) * 100);
                percentTotal += percent;
            }
            const current = srcData.current[i];
            const previous = srcData.prev[i] || 0;
            const change = previous === 0 ? "-" : Math.round(((current - previous) / previous) * 100);
            pieData.push({
                name: description, drill: k, y: current, percent, previous, change
            });
        });
        return pieData;
    }
    
    processData(data) {
        // sort current date range dataset
        const props = this.props;
        const reactProps = props.subModel.get("doublePivot").reactProps;
        const countField = reactProps.countField;
        data[0].sort((a, b) => a[countField] < b[countField]);
        const topRecords = data[0].slice(0, 4);
        const topKeys = topRecords.map((r) => r[props.second_pivot_key]);
        const theRestArray = data[0] && data[0].length ? data[0].slice(4).map((r) => r[countField]) : [];
        const theRest = theRestArray && theRestArray.length ? theRestArray.reduce((total, value) => total + value) : 0;
        const matchingRecords = [];
        const nonMatchinRecords = [];
        data[1].forEach((r) => {
            if (topKeys.includes(r[props.second_pivot_key])) {
                matchingRecords.push(r);
            } else {
                nonMatchinRecords.push(r);
            }
        });
        
        const current = topRecords.map((r) => r[countField]);
        const prev = matchingRecords.map((r) => r[countField]);
        const nonMatchingTotalArray = nonMatchinRecords.map((r) => r[countField]);
        const nonMatchingTotal = nonMatchingTotalArray && nonMatchingTotalArray.length ? nonMatchingTotalArray.reduce((total, value) => total + value) : 0;
        current.push(theRest);
        prev.push(nonMatchingTotal);
        topKeys.push("Others");
        
        return { keys: topKeys, current, prev };
    }
    
    drillDown(e, category) {
        if (category !== "Others") {
            drillThrough(this.props, category);
        }
    }
    
    reflowChart() {
        if (this.chart) {
            // this.chart.reflow();
            const height = this.getAvailableHeight();
            this.chart.setSize(null, height);
            document.querySelector("ul.pieLegendTable").style.height = `${height - 20}px`;
        }
    }
    
    // formatTooltip(tooltip, x, points) {
    //     const test = "";
    //     debugger;
    // }
    
    render() {
        const self = this;
        
        if (this.state.error) {
            return (
                <ErrorView
                    captureInSentry={false}
                    error={new FetchErrorRetry({
                        message: "Please refresh widget as data could not load",
                        customActions: [{
                            className: "retry-widget",
                            label: "Refresh widget"
                        }],
                        hideDetails: true,
                        custom: true
                    })}
                />
            );
        }
        
        if (this.state.loaded) {
            const height = this.getAvailableHeight();
            
            const tooltipProps = {
                tooltip: {
                    formatter(tooltip, x = this.x, points = this.points) {
                        if (this.point.isNull) {
                            return "Null";
                        }
                        
                        return tooltip.defaultFormatter.call(this, tooltip);
                    }
                }
            };
            
            const seriesOptions = {
                cursor: "pointer",
                point: {
                    events: {
                        click(e) {
                            self.drillDown(e, this.drill);
                        }
                    }
                },
                
                name: "Count",
                dataLabels: {
                    enabled: false
                },
                colors: colorsArr,
                innerSize: "40%"
            };
            
            const pieData = this.getPieData();
            
            const clickRow = (e) => {
                e.preventDefault();
                const category = e.currentTarget.dataset.drill;
                if (category && category !== "Others") {
                    this.drillDown(e, category);
                }
            };
            
            const tableStyle = {
                height: `${this.getAvailableHeight() - 20}px`
            };
            
            return (
                <HighchartsProvider Highcharts={Highcharts}>
                    <div className="chartArea">
                        <div className="col-sm-5">
                            <HighchartsChart callback={this.getChart}>
                                <Chart reflow="true" height={height} marginTop={10} marginRight={0} backgroundColor="transparent"/>
                                <HighchartsTooltip {...tooltipProps.tooltip} />
                                <PieSeries showInLegend={false} data={pieData} {...seriesOptions}/>
                            </HighchartsChart>
                        </div>
                        <div className="col-sm-7">
                            <ul className="pieLegendTable" style={tableStyle}>
                                <li>
                                    <div className="iconColumn" />
                                    <div className="valueColumn"> </div>
                                    <div className="valueColumn">
                                        <div className="tableHeader">
                                            <span>PRIOR PERIOD</span>
                                            <span>CHANGE</span>
                                        </div>
                                    </div>
                                </li>
                                {pieData.map((row, index) => {
                                    const iconStyle = {
                                        position: "relative",
                                        top: "-10px",
                                        color: colorsArr[index],
                                        fontSize: "10px",
                                        display: "inline-block",
                                        marginRight: "20px"
                                    };
                                
                                    const changeClass = row.change < 0 ? "good" : "bad";
                                
                                    return (
                                        <li key={`li_${this.props.pivotName}_${row.drill}`}>
                                            <a href="#" data-drill={row.drill} onClick={clickRow}>
                                                <div className="iconColumn"><i style={iconStyle} className="fas fa-circle" /></div>
                                                <div className="valueColumn">
                                                    <Tooltip title={row.name}>
                                                        <div className="pie-value">{row.name}</div>
                                                    </Tooltip>
                                                    <div className="current"><span>{number(row.y)}</span> <span className="rate">({rate(row.percent)})</span></div>
                                                </div>
                                                <div className="valueColumn">
                                                    <div className="priors">
                                                        <div>{number(row.previous)}</div>
                                                        <div className={changeClass}>{row.change === "-" ? "-%" : rate(row.change)}</div>
                                                    </div>
                                                </div>
                                            </a>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    </div>
                </HighchartsProvider>
            );
        }
        const loaderStyle = {
            paddingTop: "100px",
            width: "100%"
        };
        const style = {
            width: "100%",
            height: "100%"
        };
        
        return (<div style={style} className="chartArea"><Loader style={loaderStyle} loaded={this.state.loaded} /></div>);
    }
}
